// apps/vendor/src/App.tsx
//
// Phase 1 Wave 12 Task 95 — vendor SPA router with nested layout
// route per Task 90 admin pattern. Authenticated routes nest under
// VendorAppShellLayout so the avatar-top sidebar + top bar render
// consistently across HomePage and Settings.
//
// Public routes: auth (login/register/etc) AND application/* — vendor
// applications are public-style flows where the user has registered
// but is not yet "fully authenticated" as an active vendor. Per
// the application stepper layout (Task 84) provides its
// own header so VendorAppShell would conflict.

import { useEffect, useRef } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom';

import { NotFoundPage, RoleGuard, useAuth } from '@printway/ui';

import { useTranslation } from './lib/i18n/index.js';

import VendorAppShell from './components/VendorAppShell.js';

// Auth pages.
import LoginPage from './pages/auth/LoginPage.js';
// UAT #2658 — full-screen maintenance surface (csrfFetch redirects here on 503).
import MaintenancePage from './pages/errors/MaintenancePage.js';
import RegisterPage from './pages/auth/RegisterPage.js';
import { RegisterSurveyPage } from './pages/auth/RegisterSurveyPage.js';
import VerifyOtpPage from './pages/auth/VerifyOtpPage.js';
import Verify2FAPage from './pages/auth/Verify2FAPage.js';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage.js';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage.js';

// Application (Wave 12 Task 99 — 5-step vendor application).
import BusinessInfoPage from './pages/application/BusinessInfoPage.js';
import StoreSetupPage from './pages/application/StoreSetupPage.js';
import DocumentsPage from './pages/application/DocumentsPage.js';
import BankDetailsPage from './pages/application/BankDetailsPage.js';
import ReviewPage from './pages/application/ReviewPage.js';
import PendingReviewPage from './pages/application/PendingReviewPage.js';

// Authenticated pages.
import AddProductPage from './pages/AddProduct.js';
import AnalyticsPage from './pages/Analytics.js';
import Dashboard from './pages/Dashboard.js';
import EarningsPage from './pages/Earnings.js';
import InventoryDetailPage from './pages/InventoryDetail.js';
import InventoryPage from './pages/Inventory.js';
import NotificationsPage from './pages/Notifications.js';
import OrderDetailPage from './pages/OrderDetail.js';
import OrderScanPage from './pages/orders/OrderScanPage.js';
import OrdersPage from './pages/Orders.js';
import PayoutsPage from './pages/Payouts.js';
import PerformanceReportsPage from './pages/PerformanceReports.js';
import PrintQueuePage from './pages/PrintQueue.js';
// Phase 14.1 Wave 8 Task 37 — vendor packing-group own-scope CRUD page.
// vg-binding: CONTEXT:decisions=P14.1.D-10
// vg-binding: CONTEXT:decisions=P14.1.D-16
// vg-binding: INTERFACE-STANDARDS:error-shape
import VendorPackingGroupPage from './pages/packing/VendorPackingGroupPage.js';
import ProductDetailPage from './pages/ProductDetail.js';
import ProductEditPage from './pages/ProductEdit.js';
import ProductsPage from './pages/Products.js';
import SettingsExtendedPage from './pages/SettingsExtended.js';
import SupportPage from './pages/Support.js';
import {
  SupportAssignedDetailPage,
  SupportMineDetailPage,
} from './pages/SupportDetail.js';
import DispatchQueue from './pages/DispatchQueue.js';

// Global features
import { CommandPalette } from './features/cmdk/CommandPalette.js';
import { ProfilePage } from './pages/settings/ProfilePage.js';
import { ChangePasswordPage } from './pages/settings/ChangePasswordPage.js';
import { PreferencesPage } from './pages/settings/PreferencesPage.js';
import { Setup2FAPage } from './pages/settings/Setup2FAPage.js';
import { BillingInfoPage } from './pages/settings/BillingInfoPage.js';
import { WarehousesPage } from './pages/settings/WarehousesPage.js';
import SecurityCenterPage from './pages/settings/SecurityCenterPage.js';
// vg-binding: P18.D-21 — `/settings/integration` is a DEEP-LINKABLE route of its
// own (SettingsLayout's vendor strip links straight to it), so the page has to
// be registered here; a page with no route is unreachable code, and G-15/TS-12
// opens the URL directly.
import IntegrationSettingsPage from './pages/IntegrationSettingsPage.js';
import KycPage from './pages/settings/KycPage.js';

/**
 * VendorAppShellLayout — nested layout route. Authenticated pages render
 * via <Outlet/> inside VendorAppShell. Auth-guard hydrates user, redirects
 * /login on unauth.
 */
function VendorAppShellLayout(): JSX.Element {
  const { t } = useTranslation('common');
  const user = useAuth((s) => s.user);
  const isLoading = useAuth((s) => s.isLoading);
  const fetchMe = useAuth((s) => s.fetchMe);
  // Track whether bootstrap fetchMe was attempted. Without this, a 401
  // response (user stays null, isLoading flips false) re-triggers the
  // effect → infinite loop calling /auth/me.
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (user || attemptedRef.current) return;
    attemptedRef.current = true;
    fetchMe().catch(() => {
      // 401 → !user branch below redirects /login.
    });
  }, [user, fetchMe]);

  // UAT-1 fix (2026-05-16): only block on Loading during bootstrap (no user
  // loaded yet). Background fetchMe() refreshes set isLoading=true too —
  // gating on that would unmount the page mid-save and reset local state
  // (success toasts, form drafts).
  if ((isLoading && !user) || (!attemptedRef.current && !user)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-pw-surface">
        <div className="flex flex-col items-center gap-3">
          <div
            aria-hidden="true"
            className="h-12 w-12 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary"
          />
          <p className="text-sm text-pw-text-muted">{t('actions.loading')}</p>
        </div>
      </main>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // cross-V3 SSO — non-vendor user reaching vendor-v3 via
  // shared cookie sees <WrongPortalPage> instead of broken vendor shell.
  return (
    <RoleGuard user={user} portal="vendor">
      <VendorAppShell>
        <Outlet />
        <CommandPalette />
      </VendorAppShell>
    </RoleGuard>
  );
}

function NotFound(): JSX.Element {
  return (
    <NotFoundPage
      onHome={() => {
        window.location.assign('/');
      }}
    />
  );
}

const router = createBrowserRouter([
  // Public routes — no AppShell.
  { path: '/maintenance', element: <MaintenancePage /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/register/survey', element: <RegisterSurveyPage /> },
  { path: '/verify-otp', element: <VerifyOtpPage /> },
  { path: '/verify-2fa', element: <Verify2FAPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/index.html', element: <Navigate to="/" replace /> },

  // Vendor application 5-step flow — public-style, uses VendorApplicationStepperLayout
  // (Task 84) instead of VendorAppShell.
  { path: '/application', element: <Navigate to="/application/business-info" replace /> },
  { path: '/application/business-info', element: <BusinessInfoPage /> },
  { path: '/application/store-setup', element: <StoreSetupPage /> },
  { path: '/application/documents', element: <DocumentsPage /> },
  { path: '/application/bank-details', element: <BankDetailsPage /> },
  { path: '/application/review', element: <ReviewPage /> },
  { path: '/application/pending-review', element: <PendingReviewPage /> },

  // Authenticated layout — VendorAppShell wraps all children via <Outlet/>.
  {
    element: <VendorAppShellLayout />,
    children: [
      { path: '/', element: <Dashboard /> },
      { path: '/dashboard', element: <Dashboard /> },

      // P7 Wave 2 Task 06 — sidebar placeholders (real pages land in
      // later waves: Wave 6 / Wave 8 / Wave 9 / Wave 13).
      { path: '/orders', element: <OrdersPage /> },
      { path: '/orders/scan', element: <OrderScanPage /> },
      { path: '/orders/:orderId/items/:itemId', element: <OrderDetailPage /> },
      // Production stub retired (fix/vendor-batch-e) — the real production
      // surface is the print queue; old links land there instead of a stub.
      { path: '/production', element: <Navigate to="/print-queue" replace /> },
      // Phase 14.1 Task 37 — vendor packing-group own-scope CRUD.
      { path: '/packing-groups', element: <VendorPackingGroupPage /> },
      // Shipping Label shell retired (fix/vendor-batch-e) — labels are bought
      // and managed from the orders surface; old links redirect there.
      { path: '/shipping-label', element: <Navigate to="/orders" replace /> },
      { path: '/products', element: <ProductsPage /> },
      { path: '/products/new', element: <AddProductPage /> },
      // P7 Amendment #3 Task 83 — product detail page.
      { path: '/products/:id', element: <ProductDetailPage /> },
      // P7 Amendment #3 Task 84 — product edit page.
      { path: '/products/:id/edit', element: <ProductEditPage /> },
      { path: '/earnings', element: <EarningsPage /> },
      { path: '/payouts', element: <PayoutsPage /> },
      { path: '/analytics', element: <AnalyticsPage /> },
      { path: '/performance', element: <PerformanceReportsPage /> },
      // P7 Wave 15 polish — real Inventory FE (replaces PlaceholderPage).
      { path: '/inventory', element: <InventoryPage /> },
      // dbg-r5 Bug C — per-row drilldown.
      { path: '/inventory/:stockId', element: <InventoryDetailPage /> },
      { path: '/notifications', element: <NotificationsPage /> },
      { path: '/settings-extended', element: <SettingsExtendedPage /> },
      { path: '/print-queue', element: <PrintQueuePage /> },
      { path: '/support', element: <SupportPage /> },
      { path: '/support/tickets/:id', element: <SupportMineDetailPage /> },
      { path: '/support/assigned/:id', element: <SupportAssignedDetailPage /> },
      // Plan 2 Task 15 — dispatch queue (replaces flat assigned surface).
      { path: '/dispatches', element: <DispatchQueue /> },

      // Settings.
      { path: '/settings', element: <Navigate to="/settings/profile" replace /> },
      { path: '/settings/profile', element: <ProfilePage /> },
      { path: '/settings/kyc', element: <KycPage /> },
      { path: '/settings/change-password', element: <ChangePasswordPage /> },
      { path: '/settings/preferences', element: <PreferencesPage /> },
      { path: '/settings/2fa', element: <Setup2FAPage /> },
      { path: '/settings/billing', element: <BillingInfoPage /> },
      { path: '/settings/warehouses', element: <WarehousesPage /> },
      { path: '/settings/security', element: <SecurityCenterPage /> },
      // vg-binding: P18.D-21 — vendor Integration Settings (task-33).
      { path: '/settings/integration', element: <IntegrationSettingsPage /> },
    ],
  },

  { path: '*', element: <NotFound /> },
]);

export function App(): JSX.Element {
  return <RouterProvider router={router} />;
}
